import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { Link as RouterLink, useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import MuiBreadcrumbs from '@mui/material/Breadcrumbs'
import Link from '@mui/material/Link'
import Typography from '@mui/material/Typography'
import HomeRoundedIcon from '@mui/icons-material/HomeRounded'
import NavigateNextRoundedIcon from '@mui/icons-material/NavigateNextRounded'
import { useTranslation } from 'react-i18next'

import { useLocale } from '../../i18n/LocaleProvider'

const SITE_URL = import.meta.env.VITE_SITE_URL ?? 'https://lomasaaret.mnjlabs.com'

/**
 * The trail above every page: where you are, and one tap back to where you came from.
 *
 * WHY A CONTEXT FOR THE LAST CRUMB
 * --------------------------------
 * Two routes carry a slug - /houses/:slug and /explore/:slug - and the breadcrumb for those
 * has to read "Päätalo", not "paatalo". Only the page knows the real name, because only the
 * page has fetched it, and the name is translated per locale.
 *
 * Title-casing the slug was the obvious shortcut and it is wrong: "paatalo" would print as
 * "Paatalo" and "petajaranta" as "Petajaranta", both of which are the right letters with the
 * wrong diacritics - a misspelling of the family's own cottages, shown on every page of the
 * site. So the detail page publishes its resolved name here and the trail waits for it.
 *
 * While it is loading the trail shows the parent and stops. A crumb that appears a beat late
 * is better than one that appears wrong and corrects itself.
 */
const BreadcrumbTitleContext = createContext<(title: string | null) => void>(() => {})

export function BreadcrumbTitleProvider({ children }: { children: ReactNode }) {
  const [title, setTitle] = useState<string | null>(null)
  return (
    <BreadcrumbTitleContext.Provider value={setTitle}>
      <ResolvedTitleContext.Provider value={title}>{children}</ResolvedTitleContext.Provider>
    </BreadcrumbTitleContext.Provider>
  )
}

const ResolvedTitleContext = createContext<string | null>(null)

/**
 * Called by a detail page with the name it has just loaded. Pass null (or nothing) while the
 * fetch is still in flight.
 */
export function useBreadcrumbTitle(title: string | null | undefined) {
  const setTitle = useContext(BreadcrumbTitleContext)
  useEffect(() => {
    setTitle(title ?? null)
    // Clearing on unmount matters: without it, navigating from a cottage to /gallery would
    // leave the cottage's name sitting in the context, ready to be shown on the next slug
    // route before its own fetch resolves.
    return () => setTitle(null)
  }, [setTitle, title])
}

interface Crumb {
  label: string
  /** Absent on the last crumb - you do not link to the page you are on. */
  to?: string
}

/**
 * The section each first path segment belongs to, and the nav key that names it.
 *
 * Reusing nav.* rather than adding breadcrumb.* means these are already translated into all
 * seven languages and can never drift from the menu: if the menu says "Food & extras", so
 * does the trail.
 */
const SECTIONS: Record<string, string> = {
  houses: 'nav.houses',
  explore: 'nav.explore',
  activities: 'nav.activities',
  hotels: 'nav.hotels',
  services: 'nav.services',
  about: 'nav.about',
  gallery: 'nav.gallery',
  contact: 'nav.contact',
  booking: 'nav.bookNow',
}

export function Breadcrumbs() {
  const { pathname } = useLocation()
  const { t } = useTranslation()
  const { locale } = useLocale()
  const resolved = useContext(ResolvedTitleContext)

  const segments = useMemo(
    () => pathname.split('/').filter(Boolean),
    [pathname],
  )

  const crumbs = useMemo<Crumb[]>(() => {
    if (segments.length === 0) return []

    const [section, slug] = segments
    const sectionKey = SECTIONS[section]
    // An unknown path is a 404 or a redirect on its way somewhere. Drawing a trail for it
    // would invent a hierarchy that does not exist.
    if (!sectionKey) return []

    const trail: Crumb[] = [{ label: t('nav.home'), to: '/' }]
    trail.push(
      slug
        ? { label: t(sectionKey), to: `/${section}` }
        : { label: t(sectionKey) },
    )
    if (slug && resolved) {
      trail.push({ label: resolved })
    }
    return trail
  }, [segments, resolved, t])

  if (crumbs.length === 0) return null

  // Google reads this, not the markup: the visual trail and the structured one are generated
  // from the same array so they cannot disagree. Only linkable crumbs carry an item URL -
  // the page you are on is its own canonical, which the ListItem omits by design.
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.label,
      ...(crumb.to ? { item: `${SITE_URL}${crumb.to === '/' ? '' : crumb.to}?lang=${locale}` } : {}),
    })),
  }

  return (
    <Box
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <Container sx={{ py: 1.25 }}>
        <MuiBreadcrumbs
          aria-label={t('nav.breadcrumb')}
          separator={
            <NavigateNextRoundedIcon
              fontSize="small"
              // The chevron is direction-aware. In Hebrew the whole trail flips, and an arrow
              // still pointing right would be pointing back the way you came.
              sx={{ color: 'text.disabled', transform: 'var(--crumb-flip, none)' }}
            />
          }
          sx={{
            '--crumb-flip': (theme) => (theme.direction === 'rtl' ? 'scaleX(-1)' : 'none'),
            '& .MuiBreadcrumbs-ol': { flexWrap: 'wrap', rowGap: 0.5 },
          }}
        >
          {crumbs.map((crumb, index) =>
            crumb.to ? (
              <Link
                key={`${crumb.label}-${index}`}
                component={RouterLink}
                to={crumb.to}
                underline="hover"
                color="text.secondary"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  fontSize: 14,
                  '&:hover': { color: 'primary.main' },
                }}
              >
                {index === 0 && <HomeRoundedIcon sx={{ fontSize: 17 }} />}
                {crumb.label}
              </Link>
            ) : (
              <Typography
                key={`${crumb.label}-${index}`}
                component="span"
                aria-current="page"
                sx={{ fontSize: 14, fontWeight: 600, color: 'text.primary' }}
              >
                {crumb.label}
              </Typography>
            ),
          )}
        </MuiBreadcrumbs>
      </Container>
    </Box>
  )
}
